import React from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  IconButton,
  Divider,
  Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import AssessmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SecurityIcon from '@mui/icons-material/Security';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import api, { BASE_URL } from '../../services/api';

const SHEView = ({ data, user, onBack, onEdit }) => {
  if (!data) return null;

  const SectionHeader = ({ icon: Icon, title, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
        <Icon sx={{ color: color, mr: 1, fontSize: '1.2rem' }} />
        <Typography variant="subtitle1" sx={{ color: color, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.85rem' }}>
            {title}
        </Typography>
    </Box>
  );

  const DataField = ({ label, value, fullWidth = false }) => (
    <Grid item xs={12} md={fullWidth ? 12 : 4}>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mb: 0.5, fontWeight: '500' }}>{label}</Typography>
        <Typography sx={{ color: '#f8fafc', fontSize: '0.95rem' }}>{value || '---'}</Typography>
    </Grid>
  );

  const LongTextField = ({ label, value }) => (
    <Grid item xs={12}>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mb: 1, fontWeight: '500' }}>{label}</Typography>
        <Box sx={{ p: 2, background: 'rgba(255,255,255,0.04)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Typography sx={{ color: '#f1f5f9', lineHeight: 1.6, fontSize: '0.95rem' }}>{value || 'No information provided.'}</Typography>
        </Box>
    </Grid>
  );

  const EvidenceSection = ({ value }) => {
    const isFilePath = value && (value.startsWith('uploads/') || value.includes('uploads/'));
    const fileName = isFilePath ? value.split('/').pop() : '';

    const fileUrl = isFilePath ? `${BASE_URL}/${value}` : null;

    return (
        <Grid item xs={12}>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mb: 1, fontWeight: '500' }}>Evidence of Completion</Typography>
            <Box sx={{ p: 2, background: 'rgba(255,255,255,0.04)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.06)' }}>
                {isFilePath ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DescriptionIcon sx={{ color: '#10b981', mr: 2 }} />
                            <Box>
                                <Typography sx={{ color: '#f1f5f9', fontSize: '0.95rem', fontWeight: 'bold' }}>{fileName}</Typography>
                                <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>Uploaded Technical Report</Typography>
                            </Box>
                        </Box>
                        <Button 
                            variant="outlined" 
                            startIcon={<DownloadIcon />}
                            href={fileUrl}
                            target="_blank"
                            sx={{ 
                                color: '#10b981', 
                                borderColor: '#10b981', 
                                textTransform: 'none',
                                '&:hover': { borderColor: '#059669', background: 'rgba(16, 185, 129, 0.05)' }
                            }}
                        >
                            Download Evidence
                        </Button>
                    </Box>
                ) : (
                    <Typography sx={{ color: '#f1f5f9', lineHeight: 1.6, fontSize: '0.95rem' }}>{value || 'No evidence report provided.'}</Typography>
                )}
            </Box>
        </Grid>
    );
  };

  return (
    <Box sx={{ p: 4, maxWidth: '1200px', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={onBack} sx={{ color: 'white', mr: 2, background: 'rgba(255,255,255,0.05)', '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
            <ArrowBackIcon />
            </IconButton>
            <Box>
                <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 'bold' }}>
                    SHE Activity Detail
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>Safety record for {data.action_id}</Typography>
            </Box>
        </Box>
        {(user?.roles || []).some(role => ['admin', 'she_manager', 'she_officer', 'sys_admin'].includes(role)) && (
            <Button 
                variant="contained" 
                onClick={onEdit}
                sx={{ 
                    borderRadius: '12px', 
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    textTransform: 'none',
                    px: 4,
                    fontWeight: 'bold',
                    boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.4)'
                }}
            >
                Edit Activity
            </Button>
        )}
      </Box>

      <Paper className="glass" sx={{ p: 5, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <Grid container spacing={4}>
            {/* Identification Section */}
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <SectionHeader icon={InfoIcon} title="Identification & Context" color="#3b82f6" />
                    {data.updated_at && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, background: 'rgba(255,255,255,0.03)', px: 2, py: 0.5, borderRadius: '20px' }}>
                            <HistoryIcon sx={{ color: '#64748b', fontSize: '0.9rem', mr: 1 }} />
                            <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>Last Updated: {data.updated_at}</Typography>
                        </Box>
                    )}
                </Box>
                <Divider sx={{ borderColor: 'rgba(59, 130, 246, 0.2)', mb: 3 }} />
                <Grid container spacing={3}>
                    <DataField label="Action ID" value={data.action_id} />
                    <DataField label="Date" value={data.date} />
                    <DataField label="Quarter" value={data.quarter} />
                    <DataField label="Campus / Location" value={data.location} />
                    <DataField label="Department / Unit" value={data.department} />
                    <DataField label="Staff Group" value={data.staff_group} />
                    <DataField label="Activity Category" value={data.activity_category} />
                    <DataField label="Reference ID" value={data.reference_id} />
                </Grid>
            </Grid>

            {/* Findings Section */}
            <Grid item xs={12}>
                <SectionHeader icon={AssessmentTurnedInIcon} title="Findings & Narrative" color="#10b981" />
                <Divider sx={{ borderColor: 'rgba(16, 185, 129, 0.2)', mb: 3 }} />
                <Grid container spacing={3}>
                    <LongTextField label="Description" value={data.description} />
                    <LongTextField label="Observations" value={data.observations} />
                    <LongTextField label="Recommendations" value={data.recommendations} />
                </Grid>
            </Grid>

            {/* Management Section */}
            <Grid item xs={12}>
                <SectionHeader icon={SecurityIcon} title="Management & Resolution" color="#f59e0b" />
                <Divider sx={{ borderColor: 'rgba(245, 158, 11, 0.2)', mb: 3 }} />
                <Grid container spacing={3}>
                    <DataField label="Priority Level" value={data.priority} />
                    <DataField label="Action Owner" value={data.owner} />
                    <DataField label="Current Status" value={data.status} />
                    <EvidenceSection value={data.evidence} />
                    <LongTextField label="Verification / Sign Off" value={data.verification} />
                    <LongTextField label="Comments" value={data.comments} />
                </Grid>
            </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SHEView;
